// @noflow
import { Dog, Meal, MealInput } from '@/gql/graphql'
import { camelCase } from 'lodash'
import React, { useCallback, useState } from 'react'

import { formatCurrencyWithDecimal } from '@/utils/currency'

import useWindowSize from '@/hooks/useWindowSize'

import Card from '@/components/elements/atoms/Card/Card'
import { cloudinaryPath } from '@/components/elements/atoms/CloudinaryImage/CloudinaryImage'
import QuantitySelector from '@/components/elements/atoms/QuantitySelector/QuantitySelector'
import Text from '@/components/elements/atoms/Text'
import SwiperSlider from '@/components/elements/molecules/SwiperSlider/SwiperSlider'
import { MealInputKey } from '@/components/pages/EditRecipes/EditRecipes'
import RecipesProgressBar from '@/components/pages/EditRecipes/components/RecipesProgressBar/RecipesProgressBar'
import { sortAndFilterRecipes } from '@/components/pages/EditRecipes/utils'
import { BREAKPOINTS } from '@/components/templates/Base'

import STYLES from './RecipesMakeRoomForMore.module.sass'

import ModalLayout from '../../layout'

type Props = {
  data: {
    freshMeals: Meal[]
    onUpdate: (meals: MealInput) => void
    mealQuantities: MealInput
    numberOfPouches: number
    dogs: Dog[]
  }
}

const MealSlide = ({
  recipe,
  onChange,
  quantity: initialQuantity,
  slug,
  isFull
}: {
  recipe: Meal
  onChange: (slug: string, quantity: number) => void
  quantity: number
  slug: string
  isFull: boolean
}): JSX.Element => {
  const [quantity, setQuantity] = useState(initialQuantity)

  const handleChange = useCallback(
    (quantity: number) => {
      setQuantity(quantity)
      onChange(slug, quantity)
    },
    [onChange, slug]
  )

  const { recipeSurcharge } = recipe.flavour

  return (
    <Card padding={0} fill={false} shadow>
      <article className={STYLES.cardFlex}>
        <section>
          <div className={STYLES.cardImage}>
            <img
              src={cloudinaryPath({
                path: recipe.flavour.thumbnail.src,
                transformations: {
                  width: 164,
                  height: 152,
                  crop: 'fill',
                  dpr: 2
                }
              })}
              alt={recipe.flavour.name}
            />
          </div>
        </section>

        <section className={STYLES.cardContent}>
          <div className={STYLES.cardTitle}>
            <Text
              variant="display18"
              text={recipe.flavour.name}
              translate={false}
              margin={false}
            />

            {recipeSurcharge && recipeSurcharge > 0 ? (
              <Text
                text={'edit_recipes.recipe_surcharge'}
                namespace="account"
                variables={{
                  price: formatCurrencyWithDecimal(recipeSurcharge)
                }}
                margin={false}
              />
            ) : null}
          </div>

          <QuantitySelector
            controlled
            minValue={0}
            maxValue={isFull ? quantity : undefined}
            onChange={handleChange}
            quantity={quantity}
            variant="yellow"
            size={30}
            identifier="recipe_card_quantity"
            trackDisabledPresses
          />
        </section>
      </article>
    </Card>
  )
}

const RecipeList = ({
  recipes,
  handleUpdateMeal,
  getQuantity,
  isFull
}: {
  recipes: Meal[]
  handleUpdateMeal: (slug: string, quantity: number) => void
  getQuantity: (recipe: Meal) => number
  isFull: boolean
}): JSX.Element => {
  const { windowWidth } = useWindowSize()

  const isMobile = windowWidth <= BREAKPOINTS.sm

  if (isMobile) {
    return (
      <SwiperSlider
        slidesPerView={1.2}
        arrows={false}
        spaceBetween={16}
        className={STYLES.makeRoomModal}
      >
        {recipes.map((recipe) => {
          const slug = camelCase(recipe.flavour.slug) as MealInputKey
          const quantity = getQuantity(recipe)

          return (
            <MealSlide
              key={recipe.id}
              recipe={recipe}
              onChange={handleUpdateMeal}
              quantity={quantity}
              slug={slug}
              isFull={isFull}
            />
          )
        })}
      </SwiperSlider>
    )
  }

  return (
    <div className={STYLES.padder}>
      {recipes.map((recipe) => {
        const slug = camelCase(recipe.flavour.slug) as MealInputKey
        const quantity = getQuantity(recipe)

        return (
          <div key={recipe.id} className={STYLES.recipeCard}>
            <MealSlide
              recipe={recipe}
              onChange={handleUpdateMeal}
              quantity={quantity}
              slug={slug}
              isFull={isFull}
            />
          </div>
        )
      })}
    </div>
  )
}

const RecipesMakeRoomForMore = ({ data }: Props): JSX.Element => {
  const { onUpdate, freshMeals, mealQuantities, numberOfPouches, dogs } = data

  const [quantities, setQuantities] = useState<MealInput>(mealQuantities)

  const handleUpdateMeal = useCallback(
    (slug: string, quantity: number) => {
      setQuantities((prev) => {
        const quantities = { ...prev, [slug]: quantity }

        onUpdate(quantities)

        return quantities
      })
    },
    [onUpdate]
  )

  const getQuantity = useCallback(
    (recipe: Meal): number => {
      const slug = camelCase(recipe.slug) as MealInputKey

      const quantity = quantities[slug]
      return quantity ?? 0
    },
    [quantities]
  )

  const recipesNotZero = sortAndFilterRecipes(freshMeals, dogs)

  const totalQuantity =
    Object.values(quantities).reduce(
      (acc, curr) => (curr ? curr + (acc || 0) : acc),
      0
    ) || 0

  const percentFilled = (totalQuantity / numberOfPouches) * 100

  const isFull = totalQuantity === numberOfPouches

  const headerContent = useCallback(() => {
    return (
      <RecipesProgressBar
        percentFilled={percentFilled}
        boxIsFull={isFull}
        boxIsOver={!isFull}
        totalPouchesAdded={totalQuantity}
        numberOfPouches={numberOfPouches}
      />
    )
  }, [isFull, numberOfPouches, percentFilled, totalQuantity])

  return (
    <ModalLayout
      title={{
        namespace: 'account',
        text: 'edit_recipes.fresh_meals_page.make_room_modal.title'
      }}
      padding={false}
      HeaderContent={headerContent}
    >
      <div className={STYLES.padder}>
        <Text
          margin={false}
          namespace="account"
          text="edit_recipes.fresh_meals_page.make_room_modal.subtitle"
        />
      </div>

      <RecipeList
        recipes={recipesNotZero}
        handleUpdateMeal={handleUpdateMeal}
        getQuantity={getQuantity}
        isFull={isFull}
      />
    </ModalLayout>
  )
}

export { RecipesMakeRoomForMore }
export type { Props as MakeRoomForMorePropsData }
